import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import {
  useAccessCatalog,
  useForceLogout,
  useUpdateUserManages,
  useUpdateUserPermissions,
  useUpdateUserRole,
  useUpdateUserStatus,
} from "../../hooks/useAccessManagement";
import { ROLE_META } from "./roleMeta";
import { avatarSrc, initialsOf, stringToColor } from "./avatarColor";
import { PRIMARY_BTN_SX, SECONDARY_BTN_SX, SECTION_LABEL_SX } from "./styles";

const sortedJson = (arr) => JSON.stringify([...arr].sort());

// Opened from the users table to change one user's role, enable/disable them, edit which
// sections they can access, and (for Admins) which other users' records they can see
// alongside their own (manageUsers — see routes/paymentLink.js / paymentdashboard.js).
// Role changes and disabling are confirmed inline (via `pendingRole`) since both can
// immediately lock the affected user out.
export default function ManageAccessDialog({ user, allUsers, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  // On a phone the dialog's content (role, status, a fifteen-row permissions checklist and
  // a picker) is taller than the viewport anyway, so it takes the whole screen rather than
  // floating in a card with nowhere to scroll to.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: catalogResponse } = useAccessCatalog();
  const catalog = catalogResponse?.data || [];

  const updateRole = useUpdateUserRole();
  const updateStatus = useUpdateUserStatus();
  const updatePermissions = useUpdateUserPermissions();
  const updateManages = useUpdateUserManages();
  const forceLogout = useForceLogout();

  const [permissions, setPermissions] = useState(user.permissions || []);
  // Working state stays a plain id list — that is what the save endpoint takes — while the
  // API hands both relationship fields over as [{ id, fullName }] for rendering.
  const currentManagedIds = (user.manageUsers || []).map((entry) => entry.id);
  const [manageUsers, setManageUsers] = useState(currentManagedIds);
  const [pendingRole, setPendingRole] = useState(null);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  useEffect(() => {
    setPermissions(user.permissions || []);
    setManageUsers((user.manageUsers || []).map((entry) => entry.id));
    setConfirmingLogout(false);
  }, [user]);

  const groupedCatalog = useMemo(() => {
    const groups = {};
    for (const item of catalog) {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    }
    return groups;
  }, [catalog]);

  const isSuperAdmin = user.role === "superadmin";

  // The list is editable for everyone (plenty of non-Admins already carry one in the
  // data), but manageUsers only actually widens visibility for an Admin — routes/
  // paymentLink.js and paymentdashboard.js only consult it when `isAdmin` is true, and a
  // Super Admin sees everything regardless. Say so rather than implying it does more.
  const manageUsersHint =
    user.role === "admin"
      ? "In Payments and Campaigns, this Admin also sees records created by whoever's picked here, in addition to their own."
      : isSuperAdmin
        ? "Super Admins already see every record, so this list is kept for reference only — it doesn't widen what they can see."
        : "A User only ever sees their own records, so this list is kept for reference — it starts applying if they're made an Admin.";
  const permissionsDirty = sortedJson(permissions) !== sortedJson(user.permissions || []);
  const manageUsersDirty = sortedJson(manageUsers) !== sortedJson(currentManagedIds);

  // Who this Admin can be assigned to manage — everyone else in the org, minus themself.
  const manageableCandidates = useMemo(
    () => (allUsers || []).filter((candidate) => candidate.id !== user.id),
    [allUsers, user.id]
  );
  const selectedCandidates = manageableCandidates.filter((candidate) => manageUsers.includes(candidate.id));

  const togglePermission = (key) => {
    setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const handleRoleChange = (nextRole) => {
    // Compare against what the Select is currently showing (pendingRole || user.role), not
    // just the original user.role — otherwise re-picking the original role to undo a pending
    // change (rather than using the explicit Cancel button) is treated as a no-op and the
    // Select silently snaps back to the still-pending choice.
    if (nextRole === (pendingRole || user.role)) return;
    setPendingRole(nextRole === user.role ? null : nextRole);
  };

  const confirmRoleChange = async () => {
    try {
      await updateRole.mutateAsync({ id: user.id, role: pendingRole });
      enqueueSnackbar(`Role updated to ${ROLE_META[pendingRole].label}`, { variant: "success" });
      setPendingRole(null);
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not update role", { variant: "error" });
      setPendingRole(null);
    }
  };

  const handleStatusToggle = async (checked) => {
    try {
      const response = await updateStatus.mutateAsync({ id: user.id, isActive: checked });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(checked ? "Access enabled" : "Access disabled", { variant: "success" });
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not update status", { variant: "error" });
    }
  };

  const handleForceLogout = async () => {
    try {
      const response = await forceLogout.mutateAsync({ id: user.id });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(`${user.fullName.split(" ")[0]} has been signed out everywhere`, { variant: "success" });
      setConfirmingLogout(false);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not force sign-out", { variant: "error" });
      setConfirmingLogout(false);
    }
  };

  const handleSavePermissions = async () => {
    try {
      const response = await updatePermissions.mutateAsync({ id: user.id, permissions });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar("Permissions saved", { variant: "success" });
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not save permissions", { variant: "error" });
    }
  };

  const handleSaveManages = async () => {
    // Same rule as the "Managed by" column's picker: only ask when the save actually drops
    // someone, since unchecking a name is easy to do by accident. Pure additions save
    // straight through — pressing "Save managed users" is explicit enough on its own.
    const removed = (user.manageUsers || []).filter((person) => !manageUsers.includes(person.id));

    if (removed.length > 0) {
      const removedNames = removed.map((person) => person.fullName);
      const result = await Swal.fire({
        title: removedNames.length === 1 ? "Remove managed user?" : `Remove ${removedNames.length} managed users?`,
        html: `<div style="font-size: 14px; font-family: 'Quicksand'">
            <b>${user.fullName}</b> will no longer manage <b>${removedNames.join("</b>, <b>")}</b>.
          </div>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, save changes",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#E34C14",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;
    }

    try {
      const response = await updateManages.mutateAsync({ id: user.id, manageUsers });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar("Managed users saved", { variant: "success" });
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not save managed users", { variant: "error" });
    }
  };

  return (
    <Dialog
      open
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          src={avatarSrc(user.image)}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{
            width: 36,
            height: 36,
            fontFamily: "Quicksand",
            fontWeight: 800,
            fontSize: 13,
            bgcolor: stringToColor(user.fullName),
          }}
        >
          {initialsOf(user.fullName)}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 16, color: "#1C1C2E" }} noWrap>
            {user.fullName}
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 12, color: "#9CA3AF" }} noWrap>
            {user.email}
          </Typography>
        </Box>
        <IconButton onClick={() => onClose(false)} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography sx={SECTION_LABEL_SX}>
              Role
            </Typography>
            <Select
              fullWidth
              size="small"
              value={pendingRole || user.role}
              onChange={(e) => handleRoleChange(e.target.value)}
              disabled={updateRole.isLoading}
            >
              {Object.entries(ROLE_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </Select>

            {pendingRole && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "rgba(250,153,5,0.10)",
                  border: "1px solid rgba(250,153,5,0.35)",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Change {user.fullName.split(" ")[0]}'s role from <b>{ROLE_META[user.role].label}</b> to{" "}
                  <b>{ROLE_META[pendingRole].label}</b>?
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={confirmRoleChange}
                    disabled={updateRole.isLoading}
                    sx={PRIMARY_BTN_SX}
                  >
                    Confirm
                  </Button>
                  <Button size="small" onClick={() => setPendingRole(null)} disabled={updateRole.isLoading}>
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ ...SECTION_LABEL_SX, display: "block" }}>
                  Access
                </Typography>
                <Typography variant="body2">
                  {user.isActive ? "This user can currently sign in and use Meesha." : "This user is blocked from signing in."}
                </Typography>
              </Box>
              <Switch
                checked={user.isActive}
                disabled={updateStatus.isLoading}
                onChange={(e) => handleStatusToggle(e.target.checked)}
                color="success"
              />
            </Stack>

            {user.isActive && !confirmingLogout && (
              <Button
                size="small"
                color="inherit"
                sx={{ mt: 1, textTransform: "none" }}
                onClick={() => setConfirmingLogout(true)}
              >
                Force sign-out everywhere
              </Button>
            )}

            {confirmingLogout && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.10)",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Sign {user.fullName.split(" ")[0]} out of every device right now? Their role and status won't
                  change — they can sign back in immediately.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleForceLogout}
                    disabled={forceLogout.isLoading}
                    sx={PRIMARY_BTN_SX}
                  >
                    Sign out everywhere
                  </Button>
                  <Button size="small" onClick={() => setConfirmingLogout(false)} disabled={forceLogout.isLoading}>
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={SECTION_LABEL_SX}>
                Section access
              </Typography>
              {isSuperAdmin && <Chip size="small" label="Full access — Super Admin" color="default" />}
            </Stack>

            {isSuperAdmin ? (
              <Typography variant="body2" color="text.secondary">
                Super Admins always have access to every section and can't be restricted. Demote this
                user to Admin or User, then use this checklist to restrict what they can see — by
                default they'll keep seeing everything, same as before.
              </Typography>
            ) : (
              <>
                {Object.entries(groupedCatalog).map(([group, items]) => (
                  <Box key={group} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {group}
                    </Typography>
                    <FormGroup>
                      {items.map((item) => (
                        <FormControlLabel
                          key={item.key}
                          sx={{ ml: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={permissions.includes(item.key)}
                              onChange={() => togglePermission(item.key)}
                            />
                          }
                          label={<Typography variant="body2">{item.label}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                ))}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!permissionsDirty || updatePermissions.isLoading}
                    onClick={handleSavePermissions}
                    sx={PRIMARY_BTN_SX}
                  >
                    Save permissions
                  </Button>
                </Stack>
              </>
            )}
          </Box>

          <>
              <Divider />

              <Box>
                <Typography sx={{ ...SECTION_LABEL_SX, display: "block", mb: 0.5 }}>
                  Manages these users
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {manageUsersHint}
                </Typography>

                <Autocomplete
                  multiple
                  size="small"
                  options={manageableCandidates}
                  value={selectedCandidates}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.fullName || option.email}
                  onChange={(_, selected) => setManageUsers(selected.map((s) => s.id))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip size="small" label={option.fullName} {...getTagProps({ index })} key={option.id} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search by name or email" />
                  )}
                />

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!manageUsersDirty || updateManages.isLoading}
                    onClick={handleSaveManages}
                    sx={PRIMARY_BTN_SX}
                  >
                    Save managed users
                  </Button>
                </Stack>
              </Box>
            </>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Stored by every mutating endpoint in routes/accessManagement.js — surfaced here
            so a change always has a name and a date attached to it. */}
        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 11, color: "#9CA3AF" }}>
          {user.accessUpdatedAt
            ? `Last changed by ${user.accessUpdatedBy?.fullName || "Unknown user"} · ${new Date(
                user.accessUpdatedAt
              ).toLocaleString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}`
            : "Access has never been changed from this tab"}
        </Typography>
        <Button onClick={() => onClose(false)} sx={SECONDARY_BTN_SX}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
